import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import {
  ChevronLeft, Play, Pause, ArrowRight, CheckCheck, Zap, StopCircle, Repeat, Info,
  Music, Video, Leaf, HelpCircle, Layers, Heart, ExternalLink, Loader2, Timer, FileText,
} from "lucide-react";

const KIND_ICON = {
  audio: Music, video: Video, breathwork: Leaf, questions: HelpCircle, combo: Layers,
  body_scan: Heart, gratitude: Heart, visualization: Zap, movement: ArrowRight, check_in: Info,
  text: FileText, timer: Timer,
};

function fmtDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
}

export default function MethodPlayer() {
  const { id: mid } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [stepRemaining, setStepRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [autoChain, setAutoChain] = useState(true);
  const [chainCountdown, setChainCountdown] = useState(0);
  const timerRef = useRef(null);
  const chainTimerRef = useRef(null);
  const nextStepAutoRef = useRef(() => {});
  usePageMeta(method?.title || t("nav.unlocked_methods"));

  useEffect(() => {
    if (!mid) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/methods/${mid}/player`);
        if (alive) setMethod(data);
        api.post(`/coach/methods/${mid}/open`).catch(() => {});
      } catch (e) { toast.error(apiError(e, t("mp_load_failed"))); } finally { if (alive) setLoading(false); }
    })();
    return () => {
      alive = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (chainTimerRef.current) clearInterval(chainTimerRef.current);
    };
  }, [mid]);

  const isCombo = method?.kind === "combo";
  const steps = useMemo(() => (isCombo ? method?.steps || [] : []), [isCombo, method]);
  const currentStep = steps[stepIdx];
  const activeDuration = isCombo ? (currentStep?.duration_s || 0) : (method?.duration_min ? method.duration_min * 60 : 0);

  const finishMethod = useCallback(async () => {
    try { await api.post(`/coach/methods/${mid}/complete`); } catch { /* silent */ }
    if (timerRef.current) clearInterval(timerRef.current);
    setRunning(false);
    setShowFeedback(true);
  }, [mid]);

  const nextStep = useCallback(() => {
    if (chainTimerRef.current) clearInterval(chainTimerRef.current);
    setChainCountdown(0);
    if (!isCombo) { finishMethod(); return; }
    if (stepIdx < steps.length - 1) { setStepIdx((i) => i + 1); setRunning(false); setStepRemaining(0); }
    else finishMethod();
  }, [isCombo, stepIdx, steps.length, finishMethod]);

  useEffect(() => { nextStepAutoRef.current = nextStep; });

  const startStep = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (chainTimerRef.current) clearInterval(chainTimerRef.current);
    setChainCountdown(0);
    setStepRemaining(activeDuration);
    if (activeDuration <= 0) { setRunning(false); return; }
    setRunning(true);
    timerRef.current = setInterval(() => {
      setStepRemaining((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setRunning(false);
          if (autoChain && isCombo && stepIdx < steps.length - 1) {
            setChainCountdown(3);
            chainTimerRef.current = setInterval(() => {
              setChainCountdown((c) => {
                if (c <= 1) { clearInterval(chainTimerRef.current); setTimeout(() => nextStepAutoRef.current?.(), 0); return 0; }
                return c - 1;
              });
            }, 1000);
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [activeDuration, autoChain, isCombo, steps.length, stepIdx]);

  const cancelChain = () => { if (chainTimerRef.current) clearInterval(chainTimerRef.current); setChainCountdown(0); };

  if (loading || !method) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const Icon = KIND_ICON[isCombo ? (currentStep?.kind || "combo") : method.kind] || Layers;
  const displayTitle = isCombo ? (currentStep?.title || method.title) : method.title;
  const displayBody = isCombo ? currentStep?.body : method.description;
  const mediaUrl = isCombo ? (currentStep?.audio_url || currentStep?.video_url) : (method.audio_url || method.video_url);

  return (
    <div className="mx-auto max-w-xl" data-testid="method-player">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="mp-back" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate font-display text-base text-foreground">{method.title}</p>
          {method.coach_name ? <p className="truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("mp_by")} {method.coach_name}</p> : null}
        </div>
        <div className="w-9 shrink-0" />
      </div>

      <div className="space-y-3">
        {isCombo && steps.length > 1 ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/60 p-2.5" data-testid="mp-chain-toggle">
            <Repeat className={`h-3.5 w-3.5 ${autoChain ? "text-primary" : "text-muted-foreground"}`} />
            <span className="flex-1 text-xs font-medium text-foreground">{t("mp_auto_chain")}</span>
            <button data-testid="mp-chain-btn" onClick={() => setAutoChain((v) => !v)} className={`flex h-5 w-9 items-center rounded-full p-0.5 transition-colors ${autoChain ? "bg-primary" : "bg-border"}`}>
              <span className={`h-4 w-4 rounded-full bg-white transition-transform ${autoChain ? "translate-x-4" : ""}`} />
            </button>
          </div>
        ) : null}

        {isCombo && steps.length > 0 ? (
          <div className="flex items-center gap-1">
            {steps.map((_, i) => (
              <span key={i} className={`h-1 flex-1 rounded-full ${i < stepIdx ? "bg-primary/50" : i === stepIdx ? "bg-primary" : "bg-secondary"}`} />
            ))}
            <span className="ml-2 text-[11px] font-medium text-muted-foreground">{stepIdx + 1} / {steps.length}</span>
          </div>
        ) : null}

        <div className="flex flex-col items-center gap-2.5 rounded-xl border border-primary/30 bg-primary/10 p-5 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/60"><Icon className="h-7 w-7 text-primary" /></span>
          <p className="font-display text-xl text-foreground">{displayTitle}</p>
          {method.subtitle && !isCombo ? <p className="text-xs font-medium uppercase tracking-wide text-primary">{method.subtitle}</p> : null}
          {displayBody ? <p className="text-sm leading-relaxed text-muted-foreground">{displayBody}</p> : null}

          {activeDuration > 0 ? (
            <div className="mt-2 w-full">
              <p className="font-display text-4xl text-primary">{fmtDuration(running ? stepRemaining : activeDuration)}</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-card">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${activeDuration > 0 ? Math.max(0, Math.min(100, (1 - stepRemaining / activeDuration) * 100)) : 0}%` }} />
              </div>
              <div className="mt-3 flex justify-center">
                {!running ? (
                  <button data-testid="mp-start" onClick={startStep} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"><Play className="h-4 w-4" /> {t("mp_start")}</button>
                ) : (
                  <button data-testid="mp-pause" onClick={() => { if (timerRef.current) clearInterval(timerRef.current); setRunning(false); }} className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-card px-5 py-2 text-sm font-medium text-primary"><Pause className="h-4 w-4" /> {t("mp_pause")}</button>
                )}
              </div>
            </div>
          ) : null}

          {mediaUrl ? (
            <a href={mediaUrl} target="_blank" rel="noopener noreferrer" data-testid="mp-open-media" className="mt-2 flex w-full items-center gap-2 rounded-lg border border-border bg-card p-2.5 text-xs text-primary">
              {method.audio_url || currentStep?.audio_url ? <Music className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
              <span className="min-w-0 flex-1 truncate">{mediaUrl}</span>
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}

          {method.questions && method.questions.length > 0 && !isCombo ? (
            <div className="mt-2 w-full space-y-2">
              {method.questions.map((q, i) => (
                <div key={i} className="rounded-lg border border-border bg-card p-2.5 text-left">
                  <p className="text-sm text-foreground">{q.prompt}</p>
                  {q.required ? <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">{t("cm_q_required")}</p> : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <button data-testid="mp-next" onClick={nextStep} className={`flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-medium text-white ${chainCountdown > 0 ? "bg-orange-600" : "bg-primary"}`}>
          {chainCountdown > 0 ? <Zap className="h-4 w-4" /> : isCombo && stepIdx < steps.length - 1 ? <ArrowRight className="h-4 w-4" /> : <CheckCheck className="h-4 w-4" />}
          {chainCountdown > 0 ? `${t("mp_next_in")} ${chainCountdown}s…` : isCombo && stepIdx < steps.length - 1 ? t("mp_next_step") : t("mp_finish")}
        </button>
        {chainCountdown > 0 ? (
          <button data-testid="mp-chain-cancel" onClick={cancelChain} className="flex w-full items-center justify-center gap-1.5 py-1 text-xs font-medium text-muted-foreground"><StopCircle className="h-3.5 w-3.5" /> {t("mp_chain_cancel")}</button>
        ) : null}

        <div className="flex gap-1.5 rounded-lg bg-secondary/50 p-2.5" data-testid="mp-disclaimer">
          <Info className="h-3 w-3 shrink-0 text-muted-foreground" />
          <p className="text-[10px] italic leading-relaxed text-muted-foreground">{t("mp_disclaimer")}</p>
        </div>
      </div>

      {showFeedback ? <FeedbackModal methodId={mid} onClose={() => { setShowFeedback(false); navigate(-1); }} /> : null}
    </div>
  );
}

function FeedbackModal({ methodId, onClose }) {
  const { t } = useI18n();
  const [hearts, setHearts] = useState(5);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try { await api.post(`/methods/${methodId}/feedback`, { hearts, note: note.trim() || undefined }); } catch { /* silent */ }
    finally { setSaving(false); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" data-testid="mp-fb-modal">
      <div className="w-full max-w-sm rounded-2xl bg-background p-5">
        <Heart className="mx-auto mb-2 h-9 w-9 text-primary" fill="currentColor" />
        <h2 className="text-center font-display text-lg text-foreground">{t("mp_fb_title")}</h2>
        <p className="mt-1 text-center text-xs text-muted-foreground">{t("mp_fb_sub")}</p>
        <div className="my-4 flex justify-center gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`mp-heart-${n}`} onClick={() => setHearts(n)}>
              <Heart className="h-7 w-7" style={{ color: n <= hearts ? "#E5637A" : "hsl(var(--muted-foreground))" }} fill={n <= hearts ? "#E5637A" : "none"} />
            </button>
          ))}
        </div>
        <textarea data-testid="mp-fb-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={t("mp_fb_note_ph")} className="min-h-[60px] w-full rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground outline-none" />
        <div className="mt-3 flex gap-2">
          <button data-testid="mp-fb-skip" onClick={onClose} className="flex-1 rounded-full bg-secondary/60 py-2.5 text-sm font-medium text-foreground">{t("mp_fb_skip")}</button>
          <button data-testid="mp-fb-submit" onClick={submit} disabled={saving} className="flex flex-[2] items-center justify-center gap-1.5 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("mp_fb_send")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
